import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR, { mutate } from "swr";
import dayjs from "dayjs";

import { getProvinceOptionList, toProv } from "../../components/Analytics/PriceReports/provinceUtils";
import {
  acceptPriceHistoryRecord,
  fetchDistinctCounts,
  fetchPublishedProducts,
  restorePriceHistoryRecord,
  updatePriceHistoryRecord,
  voidPriceHistoryRecord,
} from "../../services/beefAiPricing";
import {
  DEFAULT_VERIFY_PRICE_SORT,
  fetchVerifyPriceCounts,
  fetchVerifyPricePage,
  fetchVerifyPriceRowsByIds,
  fetchVerifyPriceScanRows,
} from "../../services/beefAiVerifyPrice";
import {
  DEFAULT_PRICING_REPORT_PROVIDERS_INCLUDE as DEFAULT_PROVIDERS,
  DEFAULT_PRICING_REPORT_UNIT,
  formatProductOptionLabel,
  formatProviderOptionLabel,
} from "../pricing-reports/constants";
import {
  productIdsForPriceHistoryFilters,
  retailersForPriceHistoryFilters,
} from "../pricing-reports/pricingReportProductFilter";
import { mapPriceHistoryBreakdownRowsToDisplayItems } from "../pricing-reports/priceHistoryBreakdownDisplay";
import {
  detectVerifyPriceAnomalies,
  formatAnomalySummaryMessage,
  summarizeAnomalies,
} from "./verifyPriceAnomalies";
import {
  DEFAULT_VERIFY_PRICE_PAGE_SIZE,
  planVerifyPriceAdvance,
  sortVerifyPriceItems,
} from "./verifyPriceUtils";

const swrOptions = { revalidateOnFocus: false, keepPreviousData: true };

export function useVerifyPricePanelState({ queriesEnabled }) {
  const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(4, "month").startOf("day"));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf("day"));
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProvidersInclude, setSelectedProvidersInclude] = useState(
    () => [...DEFAULT_PROVIDERS]
  );
  const [humanVerifiedView, setHumanVerifiedView] = useState("unverified");
  const [sort, setSort] = useState(DEFAULT_VERIFY_PRICE_SORT);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_VERIFY_PRICE_PAGE_SIZE);
  const [selectedId, setSelectedId] = useState(null);
  const [panelMode, setPanelMode] = useState("view");
  const [pendingPageSelect, setPendingPageSelect] = useState(null);
  const [actionError, setActionError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [anomalyItems, setAnomalyItems] = useState(null);
  const [anomalyNotice, setAnomalyNotice] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const anomalyScanRef = useRef(0);

  const startDateForApi = dateFrom && dateFrom.isValid() ? dateFrom.format("YYYY-MM-DD") : "";
  const endDateForApi = dateTo && dateTo.isValid() ? dateTo.format("YYYY-MM-DDTHH:mm:ss.SSSZ") : "";
  const queriesReady = Boolean(queriesEnabled && startDateForApi && endDateForApi);

  const selectedProductIds = useMemo(
    () => productIdsForPriceHistoryFilters(selectedProducts) ?? [],
    [selectedProducts]
  );
  const selectedRetailerNames = useMemo(
    () => retailersForPriceHistoryFilters(selectedRetailers) ?? [],
    [selectedRetailers]
  );

  const filterOptionsSwrKey = queriesReady
    ? ["verify-price-filters", startDateForApi, endDateForApi]
    : null;

  const { data: distinctFilterCounts, error: filterOptionsError } = useSWR(
    filterOptionsSwrKey,
    async () => {
      const dateRange = { dateFrom: startDateForApi, dateTo: endDateForApi };
      const [provinces, retailers, providers] = await Promise.all([
        fetchDistinctCounts("province", dateRange),
        fetchDistinctCounts("retailer", dateRange),
        fetchDistinctCounts("provider", dateRange),
      ]);
      return { provinces, retailers, providers };
    },
    { revalidateOnFocus: false }
  );

  const { data: publishedProducts, error: publishedProductsError } = useSWR(
    queriesEnabled ? "verify-price-products-catalog" : null,
    () => fetchPublishedProducts({ withRefIdOnly: true }),
    { revalidateOnFocus: false, dedupingInterval: 60 * 60 * 1000 }
  );

  const provinceCodeToBeefAiProvinceString = useMemo(() => {
    const provinceCodeToRawString = new Map();
    for (const row of distinctFilterCounts?.provinces || []) {
      const rawProvince = row.province;
      const provinceCode = toProv(rawProvince);
      if (provinceCode && rawProvince != null && String(rawProvince).trim() && !provinceCodeToRawString.has(provinceCode)) {
        provinceCodeToRawString.set(provinceCode, String(rawProvince).trim());
      }
    }
    return provinceCodeToRawString;
  }, [distinctFilterCounts]);

  const provinceChoices = useMemo(() => {
    const provinceCodes = [
      ...new Set(
        (distinctFilterCounts?.provinces || [])
          .map((row) => toProv(row.province))
          .filter(Boolean)
      ),
    ];
    return getProvinceOptionList(provinceCodes);
  }, [distinctFilterCounts]);

  const provinceFilterForApi = useMemo(() => {
    if (selectedProvince == null) return undefined;
    return provinceCodeToBeefAiProvinceString.get(selectedProvince.value) ?? selectedProvince.value;
  }, [selectedProvince, provinceCodeToBeefAiProvinceString]);

  const retailerChoices = useMemo(() => {
    const retailerNames = (distinctFilterCounts?.retailers || [])
      .map((row) => row.retailer)
      .filter((name) => name != null && String(name).trim());
    return [...new Set(retailerNames)]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((name) => ({ name: String(name) }));
  }, [distinctFilterCounts]);

  const providerChoices = useMemo(() => {
    const providerNames = (distinctFilterCounts?.providers || [])
      .map((row) => row.provider)
      .filter((name) => name != null && String(name).trim());
    return [...new Set(providerNames)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [distinctFilterCounts]);

  const productFilterChoices = useMemo(() => {
    const uniqueProductsById = new Map();
    for (const catalogProduct of publishedProducts || []) {
      if (catalogProduct?.id && String(catalogProduct.ref_id || "").trim()) {
        uniqueProductsById.set(catalogProduct.id, catalogProduct);
      }
    }
    return [...uniqueProductsById.values()].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    );
  }, [publishedProducts]);

  useEffect(() => {
    if (!providerChoices.length) return;
    setSelectedProvidersInclude((current) => {
      const validSelection = current.filter((provider) => providerChoices.includes(provider));
      if (validSelection.length > 0) return validSelection;
      return DEFAULT_PROVIDERS.filter((provider) => providerChoices.includes(provider));
    });
  }, [providerChoices]);

  useEffect(() => {
    if (!productFilterChoices.length) return;
    setSelectedProducts((current) =>
      current.filter((product) => productFilterChoices.some((choice) => choice.id === product.id))
    );
  }, [productFilterChoices]);

  useEffect(() => {
    if (!retailerChoices.length) return;
    setSelectedRetailers((current) =>
      current.filter((retailer) => retailerChoices.some((choice) => choice.name === retailer.name))
    );
  }, [retailerChoices]);

  const queryFilters = useMemo(
    () => ({
      dateFrom: startDateForApi || undefined,
      dateTo: endDateForApi || undefined,
      province: provinceFilterForApi,
      retailers: selectedRetailerNames,
      productIds: selectedProductIds,
      providersInclude: selectedProvidersInclude,
      unitType: "weight",
      unit: DEFAULT_PRICING_REPORT_UNIT,
      isDiscounted: false,
    }),
    [
      startDateForApi,
      endDateForApi,
      provinceFilterForApi,
      selectedRetailerNames,
      selectedProductIds,
      selectedProvidersInclude,
    ]
  );

  useEffect(() => {
    setPage(0);
    setSelectedId(null);
    setPanelMode("view");
    setActionError("");
  }, [queryFilters, humanVerifiedView, sort]);

  useEffect(() => {
    anomalyScanRef.current += 1;
    setAnomalyItems(null);
    setAnomalyNotice(null);
    setAnomalyLoading(false);
  }, [queryFilters, humanVerifiedView]);

  const countsSwrKey = queriesReady ? ["verify-price-counts", queryFilters] : null;
  const {
    data: counts,
    error: countsError,
    isLoading: isCountsLoading,
  } = useSWR(countsSwrKey, () => fetchVerifyPriceCounts(queryFilters), swrOptions);

  const pageSwrKey = queriesReady
    ? ["verify-price-page", queryFilters, page, rowsPerPage, sort, humanVerifiedView]
    : null;
  const {
    data: pagePayload,
    error: pageError,
    isLoading: isPageLoading,
  } = useSWR(
    pageSwrKey,
    () =>
      fetchVerifyPricePage(queryFilters, {
        page: page + 1,
        limit: rowsPerPage,
        sort,
        humanVerifiedView,
      }),
    swrOptions
  );

  const rows = pagePayload?.rows || [];
  const pageRows = useMemo(
    () => mapPriceHistoryBreakdownRowsToDisplayItems(rows),
    [rows]
  );
  const anomalyMode = anomalyItems != null;
  const sortedAnomalyItems = useMemo(
    () => (anomalyMode ? sortVerifyPriceItems(anomalyItems, sort) : []),
    [anomalyMode, anomalyItems, sort]
  );
  const displayItems = useMemo(() => {
    if (!anomalyMode) return pageRows;
    const start = page * rowsPerPage;
    return sortedAnomalyItems.slice(start, start + rowsPerPage);
  }, [anomalyMode, pageRows, sortedAnomalyItems, page, rowsPerPage]);
  const totalCount = anomalyMode ? sortedAnomalyItems.length : pagePayload?.totalCount ?? 0;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, totalCount, rowsPerPage]);

  useEffect(() => {
    if (!pendingPageSelect || !displayItems.length) return;
    if (pendingPageSelect === "first") {
      setSelectedId(displayItems[0].id);
    } else if (pendingPageSelect === "last") {
      setSelectedId(displayItems[displayItems.length - 1].id);
    }
    setPendingPageSelect(null);
    setPanelMode("view");
  }, [pendingPageSelect, displayItems]);

  const selectedItem = useMemo(
    () => displayItems.find((item) => item.id === selectedId) || null,
    [displayItems, selectedId]
  );

  const dialogOpen = Boolean(selectedId);

  const resolveProvinceForApi = useCallback(
    (provinceCode) => {
      if (!provinceCode) return "";
      return provinceCodeToBeefAiProvinceString.get(provinceCode) ?? provinceCode;
    },
    [provinceCodeToBeefAiProvinceString]
  );

  const unitChoices = useMemo(() => {
    const units = new Set(["kg"]);
    for (const row of rows) {
      const unit = typeof row?.unit === "string" ? row.unit.trim() : "";
      if (unit) units.add(unit);
    }
    return [...units].sort((a, b) => String(a).localeCompare(String(b)));
  }, [rows]);

  const revalidateLists = useCallback(async () => {
    await Promise.all(
      [pageSwrKey, countsSwrKey].filter(Boolean).map((key) => mutate(key))
    );
  }, [pageSwrKey, countsSwrKey]);

  const applyAdvance = useCallback(
    (direction, { afterRemove = false } = {}) => {
      const usingAnomalies = anomalyItems != null;
      const navigationRows = usingAnomalies
        ? sortVerifyPriceItems(anomalyItems, sort)
        : displayItems;
      const navigationCount = usingAnomalies ? navigationRows.length : totalCount;
      const planned = planVerifyPriceAdvance({
        rows: navigationRows,
        currentId: selectedId,
        direction,
        page: usingAnomalies ? 0 : page,
        totalCount: afterRemove ? Math.max(0, navigationCount - 1) : navigationCount,
        pageSize: usingAnomalies ? Math.max(navigationRows.length, 1) : rowsPerPage,
      });

      if (planned.type === "select") {
        setSelectedId(planned.id);
        setPanelMode("view");
        return;
      }
      if (planned.type === "page") {
        setPendingPageSelect(planned.select);
        setPage(planned.page);
        return;
      }
      setSelectedId(null);
      setPanelMode("view");
    },
    [anomalyItems, sort, displayItems, selectedId, page, totalCount, rowsPerPage]
  );

  const removeAnomalyItem = useCallback((recordId) => {
    setAnomalyItems((current) => {
      if (!current) return current;
      return current.filter((item) => item.id !== recordId);
    });
  }, []);

  const handleClearAnomalies = useCallback(() => {
    anomalyScanRef.current += 1;
    setAnomalyItems(null);
    setAnomalyNotice(null);
    setAnomalyLoading(false);
    setPage(0);
    setSelectedId(null);
    setPanelMode("view");
  }, []);

  const handleFindAnomalies = useCallback(async () => {
    if (humanVerifiedView === "voided") return;
    if (anomalyLoading || !queriesReady) return;
    if (anomalyItems != null) {
      handleClearAnomalies();
      return;
    }

    const scanId = anomalyScanRef.current + 1;
    anomalyScanRef.current = scanId;
    setAnomalyLoading(true);
    setAnomalyNotice(null);
    setActionError("");

    try {
      const scanRows = await fetchVerifyPriceScanRows(queryFilters);
      if (scanId !== anomalyScanRef.current) return;

      const findings = detectVerifyPriceAnomalies(scanRows, { humanVerifiedView });
      const summary = summarizeAnomalies(findings);
      const message = formatAnomalySummaryMessage(summary);

      if (!findings.length) {
        setAnomalyItems(null);
        setAnomalyNotice({ type: "empty", message });
        return;
      }

      const detailRows = await fetchVerifyPriceRowsByIds(findings.map((finding) => finding.id));
      if (scanId !== anomalyScanRef.current) return;

      const findingById = new Map(findings.map((finding) => [String(finding.id), finding]));
      const items = mapPriceHistoryBreakdownRowsToDisplayItems(detailRows)
        .map((item) => {
          const anomaly = findingById.get(String(item.id));
          return anomaly ? { ...item, anomaly } : null;
        })
        .filter(Boolean);

      setAnomalyItems(items);
      setAnomalyNotice({ type: "results", message, summary });
      setPage(0);
      setSelectedId(null);
      setPanelMode("view");
    } catch (error) {
      if (scanId !== anomalyScanRef.current) return;
      setAnomalyItems(null);
      setAnomalyNotice({
        type: "error",
        message: error?.message || "Something went wrong while scanning for anomalies.",
      });
    } finally {
      if (scanId === anomalyScanRef.current) {
        setAnomalyLoading(false);
      }
    }
  }, [
    anomalyLoading,
    anomalyItems,
    handleClearAnomalies,
    queryFilters,
    humanVerifiedView,
    queriesReady,
  ]);

  const handleCloseDialog = useCallback(() => {
    setSelectedId(null);
    setPanelMode("view");
    setActionError("");
  }, []);

  const handleCardClick = useCallback((item) => {
    setSelectedId(item.id);
    setPanelMode("view");
    setActionError("");
  }, []);

  const handleSkip = useCallback(
    (direction = 1) => {
      applyAdvance(direction);
    },
    [applyAdvance]
  );

  const handleAccept = useCallback(async (recordId) => {
    const id = typeof recordId === "string" && recordId ? recordId : selectedId;
    if (!id || actionLoading) return;
    setActionLoading(true);
    setActionError("");
    try {
      await acceptPriceHistoryRecord(id);
      if (selectedId === id) {
        applyAdvance(1, { afterRemove: humanVerifiedView === "unverified" || anomalyItems != null });
      }
      removeAnomalyItem(id);
      await revalidateLists();
    } catch (error) {
      setActionError(error?.message || "Failed to accept this price record.");
    } finally {
      setActionLoading(false);
    }
  }, [selectedId, actionLoading, applyAdvance, humanVerifiedView, anomalyItems, removeAnomalyItem, revalidateLists]);

  const handleReject = useCallback(async (recordId) => {
    const id = typeof recordId === "string" && recordId ? recordId : selectedId;
    if (!id || actionLoading) return;
    setActionLoading(true);
    setActionError("");
    try {
      await voidPriceHistoryRecord(id);
      if (selectedId === id) {
        applyAdvance(1, { afterRemove: true });
      }
      removeAnomalyItem(id);
      await revalidateLists();
    } catch (error) {
      setActionError(error?.message || "Failed to reject this price record.");
    } finally {
      setActionLoading(false);
    }
  }, [selectedId, actionLoading, applyAdvance, removeAnomalyItem, revalidateLists]);

  const handleRestore = useCallback(async (recordId) => {
    const id = typeof recordId === "string" && recordId ? recordId : selectedId;
    if (!id || actionLoading) return;
    setActionLoading(true);
    setActionError("");
    try {
      await restorePriceHistoryRecord(id);
      if (selectedId === id) {
        applyAdvance(1, { afterRemove: true });
      }
      removeAnomalyItem(id);
      await revalidateLists();
    } catch (error) {
      setActionError(error?.message || "Failed to restore this price record.");
    } finally {
      setActionLoading(false);
    }
  }, [selectedId, actionLoading, applyAdvance, removeAnomalyItem, revalidateLists]);

  const handleSaveRecord = useCallback(
    async (recordId, formValues) => {
      await updatePriceHistoryRecord(recordId, formValues);
      applyAdvance(1, { afterRemove: humanVerifiedView === "unverified" || anomalyItems != null });
      removeAnomalyItem(recordId);
      await revalidateLists();
    },
    [applyAdvance, humanVerifiedView, anomalyItems, removeAnomalyItem, revalidateLists]
  );

  const handleOpenEdit = useCallback(() => {
    setPanelMode("edit");
  }, []);

  const isLoadingFilterOptions = Boolean(filterOptionsSwrKey) && !distinctFilterCounts && !filterOptionsError;
  const isPublishedProductsLoading = Boolean(queriesEnabled) && !publishedProducts && !publishedProductsError;
  const pageInitialLoading = isPageLoading && pagePayload === undefined;
  const pageRefreshing = isPageLoading && pagePayload !== undefined;

  return {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    provinceChoices,
    selectedProvince,
    setSelectedProvince,
    productFilterChoices,
    selectedProducts,
    setSelectedProducts,
    retailerChoices,
    selectedRetailers,
    setSelectedRetailers,
    providerChoices,
    selectedProvidersInclude,
    setSelectedProvidersInclude,
    humanVerifiedView,
    setHumanVerifiedView,
    sort,
    setSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    displayItems,
    totalCount,
    counts: counts || { verifiedCount: 0, unverifiedCount: 0, totalCount: 0 },
    isCountsLoading: isCountsLoading && counts === undefined,
    isLoadingFilterOptions,
    isPublishedProductsLoading,
    pageInitialLoading,
    pageRefreshing,
    anyFetchError:
      filterOptionsError?.message ||
      publishedProductsError?.message ||
      countsError?.message ||
      pageError?.message ||
      "",
    selectedItem,
    selectedId,
    dialogOpen,
    panelMode,
    setPanelMode,
    actionError,
    actionLoading,
    unitChoices,
    resolveProvinceForApi,
    formatProductOptionLabel,
    formatProviderOptionLabel,
    handleCardClick,
    handleCloseDialog,
    handleAccept,
    handleReject,
    handleRestore,
    handleSkip,
    handleSaveRecord,
    handleOpenEdit,
    anomalyMode,
    anomalyLoading,
    anomalyNotice,
    handleFindAnomalies,
  };
}
